import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import "./ForgotPassword.css";

const API_BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:8000"
    : process.env.REACT_APP_API_URL;

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = location.state?.email || "";
  const emailFromStorage = localStorage.getItem("forgotPasswordEmail") || "";
  const email = (emailFromState || emailFromStorage).trim();

  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = OTP verification, 2 = Password reset

  useEffect(() => {
    if (email) {
      localStorage.setItem("forgotPasswordEmail", email);
    }
  }, [email]);

  const handleResendOtp = async () => {
    setMessage("");
    setError("");

    if (!email) {
      return setError("Email session expired. Please start again.");
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE_URL}/api/auth/forgot-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to resend OTP.");
      }

      setOtp("");
      setStep(1);
      setMessage(data.message || "OTP resent successfully. Check your inbox.");
    } catch (err) {
      setError(err.message || "Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!otp.trim()) {
      return setError("OTP is required.");
    }

    if (otp.length < 4) {
      return setError("OTP must be at least 4 digits.");
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE_URL}/api/auth/verify-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, otp, type: "password-reset" }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "OTP verification failed.");
      }

      setMessage("✅ OTP verified successfully! Enter your new password.");
      setStep(2);
    } catch (err) {
      setError(err.message || "OTP verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!newPassword.trim()) {
      return setError("New password is required.");
    }

    if (newPassword.length < 6) {
      return setError("Password must be at least 6 characters.");
    }

    if (newPassword !== confirmPassword) {
      return setError("Passwords do not match.");
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE_URL}/api/auth/reset-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, otp, newPassword, confirmPassword }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Password reset failed.");
      }

      setMessage("✅ Password reset successfully! Redirecting to login...");
      localStorage.removeItem("forgotPasswordEmail");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      setError(err.message || "Password reset failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!email) {
    return (
      <div className="forgot-main-container">
        <div className="forgot-container">
          <div className="forgot-left-panel">
            <h1>Password Recovery</h1>
            <p>Secure your account with OTP verification</p>
          </div>
          <div className="forgot-right-panel">
            <div className="error-message">
              ❌ Session expired. Please try again.
            </div>
            <Link to="/forgot-password">
              <button>Back to Forgot Password</button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-main-container">
      <div className="forgot-container">

        {/* LEFT PANEL */}
        <div className="forgot-left-panel">
          <h1>Password Recovery</h1>
          <p>Secure your account with OTP verification</p>
        </div>

        {/* RIGHT PANEL */}
        <div className="forgot-right-panel">
          <h3>Hello!</h3>
          <h2>Reset Your Password</h2>
          <p className="forgot-text">
            Email: <span>{email}</span>
          </p>

          <button type="button" onClick={handleResendOtp} disabled={loading}>
            {loading ? "Resending..." : "Resend OTP"}
          </button>

          {/* STEP 1: OTP VERIFICATION */}
          {step === 1 && (
            <form onSubmit={handleVerifyOtp}>
              <div>
                <label>Enter OTP</label>
                <input
                  type="text"
                  placeholder="Enter 4-6 digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength="6"
                />
              </div>

              <button type="submit" disabled={loading}>
                {loading ? "Verifying..." : "Verify OTP"}
              </button>
            </form>
          )}

          {/* STEP 2: PASSWORD RESET */}
          {step === 2 && (
            <form onSubmit={handleResetPassword}>
              <div>
                <label>New Password</label>
                <input
                  type="password"
                  placeholder="Enter new password (min 6 chars)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              <div>
                <label>Confirm Password</label>
                <input
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              <button type="submit" disabled={loading}>
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          )}

          {message && (
            <div className="message-success">✅ {message}</div>
          )}

          {error && (
            <div className="error-message">❌ {error}</div>
          )}

          <p className="forgot-link">
            Remember your password? <Link to="/login">Login here</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
